import { TextField, Stack, CircularProgress } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Widget, { type IconButtonConfig } from '@/components/common/widget.tsx';
import { useTranslations } from '@/i18n/use-translations.tsx';
import { useNotifications } from '@/hooks/use-notifications';
import { updateFlowSlotBreakSchemaFactory } from '@/schemas/flows/slots/update-break';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import { useEffect, useState, type ReactNode } from 'react';
import { useFlowSyncTransport } from '@/state/mutations/flows/use-flow-sync-transport';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useFlowMode } from '../../use-flow-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import { minuteOffsetToTime } from '@/components/events/helpers/minute-offset-to-time.helper';
import { calculateFlowDurationFromSlots } from '@/components/flows/detail/slot-list/calculate';
import { useSearchParams } from 'react-router';

export const UpdateBreakForm = ({
  slot,
  widgetButton,
}: {
  slot: FlowSlotDto<'Break'>;
  widgetButton?: IconButtonConfig | ReactNode;
}) => {
  const t = useTranslations();
  const notification = useNotifications();
  const { emitFlowSync, canEmit } = useFlowSyncTransport();
  const flow = useFlowStore((s) => s.flow);
  const [updatePending, setUpdatePending] = useState(false);
  const mode = useFlowMode();
  const [searchParams] = useSearchParams();
  const eventSlotIdParam = searchParams.get('eventSlotId');

  const updateFlowBreakSlot = async (breakData: BreakFormType) => {
    if (!flow) return;
    const updatedSlots = (flow.slots ?? []).map((iteratedSlot) =>
      iteratedSlot.id === slot.id ? { ...iteratedSlot, title: breakData.title } : iteratedSlot,
    );
    const normalizedSlots = updatedSlots.map((iteratedSlot) => ({
      ...iteratedSlot,
      content: typeof iteratedSlot.content === 'object' ? iteratedSlot.content?.id : iteratedSlot.content,
    }));

    let startTime: string | undefined;
    let endTime: string | null | undefined;
    let eventDayId: string | undefined;
    let eventSlotId: string | undefined;

    if (eventSlotIdParam) {
      if (flow.slot?.startOffsetMinutes !== undefined) {
        startTime = minuteOffsetToTime(flow.slot.startOffsetMinutes);
        const flowDurationPlannedMinutes = flow.durationPlannedMinutes;
        if (flowDurationPlannedMinutes !== undefined && flowDurationPlannedMinutes > 0) {
          endTime = minuteOffsetToTime(flow.slot.startOffsetMinutes + flowDurationPlannedMinutes);
        } else {
          endTime = null;
        }
      }
      if (flow.slot?.day?.id) {
        eventDayId = flow.slot.day.id;
      }
      if (flow.slot?.id) {
        eventSlotId = flow.slot.id;
      }
    } else {
      startTime = minuteOffsetToTime(0);
      const timedSlots = updatedSlots.filter(
        (iteratedSlot) =>
          !(
            iteratedSlot.isUntimed ??
            (iteratedSlot.plannedOffsetMinutes === undefined && iteratedSlot.computedOffsetMinutes === undefined)
          ),
      );
      const durationToUse = flow.durationPlannedMinutes ?? calculateFlowDurationFromSlots(timedSlots);
      endTime = durationToUse !== undefined ? minuteOffsetToTime(durationToUse) : null;
    }

    const flowPayload = {
      ...flow,
      slots: normalizedSlots,
      slot: undefined,
      startDate: undefined,
      ...(startTime && { startTime }),
      ...(startTime !== undefined && { endTime: endTime ?? null }),
      ...(eventSlotId && eventDayId && { eventDayId }),
      ...(eventSlotId && { eventSlotId }),
    };

    if (!canEmit) {
      notification.notifyError(t('flows.detail.slotList.detail.updateBreak.notification.updateError'));
      setUpdatePending(false);
      return;
    }
    const { ok } = await emitFlowSync({ flow: flowPayload as Record<string, unknown> });
    if (ok) {
      notification.notifySuccess(
        t('flows.detail.slotList.detail.updateBreak.notification.updateSuccess', { title: breakData.title }),
      );
    } else {
      notification.notifyError(t('flows.detail.slotList.detail.updateBreak.notification.updateError'));
    }
    setUpdatePending(false);
  };

  const breakFormSchema = updateFlowSlotBreakSchemaFactory(t);
  type BreakFormType = z.infer<typeof breakFormSchema>;
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<BreakFormType>({
    resolver: zodResolver(breakFormSchema),
    defaultValues: {
      title: slot.title,
    },
  });

  useEffect(() => {
    reset({ title: slot.title });
  }, [slot.title, reset]);

  const onSubmit = async (data: BreakFormType) => {
    setUpdatePending(true);
    await updateFlowBreakSlot(data);
  };

  return (
    <Widget
      title={t('common.basics')}
      primaryButton={{
        label: t('common.save'),
        onClick: handleSubmit(onSubmit),
        testId: 'save-updated-break-button',
        startIcon: updatePending ? <CircularProgress size={20} /> : undefined,
        disabled: !isDirty || isOrganizeMode(mode),
        variant: isDirty ? 'contained' : 'outlined',
      }}
      iconButtons={
        widgetButton
          ? typeof widgetButton === 'object' && 'icon' in widgetButton
            ? [widgetButton as IconButtonConfig]
            : widgetButton
          : undefined
      }
    >
      <Stack component="form" onSubmit={handleSubmit(onSubmit)} gap={1} sx={{ flex: 1 }}>
        <Controller
          name="title"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              placeholder={t('flows.detail.slotList.detail.updateBreak.title')}
              variant="standard"
              label="Title"
              disabled={isOrganizeMode(mode)}
              error={!!errors.title}
              helperText={errors.title?.message}
            />
          )}
        />
      </Stack>
    </Widget>
  );
};
